import { useState } from 'react';
import { PENALTY_CODE_LABEL } from '@flm9/i18n/labels';
import type { PenaltyCode } from '@flm9/sync';
import { formatPenaltyPoints, type PenaltyValues } from '../lib/penalties';

const CODES: readonly PenaltyCode[] = ['procedural', 'miss', 'no_shoot', 'extra_shot'];

/**
 * Bottom sheet "Strafe vergeben" (same style as DqReasonSheet): one large
 * button per penalty type with its points from the stage's scoring schema.
 * One tap records the penalty; the optional note goes along with it. The
 * server derives the points itself — `values` is only the preview.
 */
export function PenaltySheet({
  shooterLabel,
  values,
  onCancel,
  onPick,
}: {
  shooterLabel: string;
  values: PenaltyValues | undefined;
  onCancel: () => void;
  /** Resolves when the penalty is queued; the parent closes the sheet. */
  onPick: (code: PenaltyCode, note: string | undefined) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function pick(code: PenaltyCode) {
    if (busy) return;
    setBusy(true);
    try {
      await onPick(code, note.trim() || undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center"
      onClick={() => !busy && onCancel()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="penalty-sheet-title"
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-zinc-900 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="penalty-sheet-title" className="text-lg font-bold">
          Strafe vergeben
        </h3>
        <p className="mb-3 truncate text-sm text-zinc-400">{shooterLabel} — Art wählen</p>
        {noteOpen ? (
          <label className="mb-3 block">
            <span className="text-sm text-zinc-400">Notiz (optional)</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              autoFocus
              disabled={busy}
              placeholder="z. B. Fußfehler an Position 2"
              className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-3 text-base"
            />
          </label>
        ) : (
          <button
            type="button"
            onClick={() => setNoteOpen(true)}
            disabled={busy}
            className="mb-3 min-h-12 text-sm text-zinc-400 underline hover:text-zinc-200 disabled:opacity-50"
          >
            + Notiz hinzufügen
          </button>
        )}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {CODES.map((code) => {
            const points = values?.[code];
            return (
              <button
                key={code}
                type="button"
                onClick={() => void pick(code)}
                disabled={busy}
                className="flex min-h-16 w-full items-center justify-between gap-3 rounded-lg border-2 border-orange-700 bg-orange-950/40 px-4 py-3 text-left font-bold text-orange-100 hover:border-orange-500 active:bg-orange-900/60 disabled:opacity-50"
              >
                <span className="text-base">{PENALTY_CODE_LABEL[code]}</span>
                {points != null && (
                  <span className="shrink-0 text-xl tabular-nums">
                    {formatPenaltyPoints(points)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {!values && (
          <p className="mt-2 text-xs text-zinc-500">
            Die Punkte legt das Wertungsschema fest; sie erscheinen nach der Übertragung.
          </p>
        )}
        <div className="mt-4 flex justify-start">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="min-h-12 rounded border border-zinc-700 px-4 py-2 text-sm text-zinc-300 disabled:opacity-30"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
